import { useEffect, useMemo, useState } from 'react'
import { ServiceDTO } from '../interfaces/Services/Services'
import { DeliveryEnum, PaymentModeId } from '../interfaces'
// API Django: GET /api/service con filtros combinables. Reemplaza a los tres
// endpoints anteriores (by-payment-mode, by-delivery-type, locked-by-application),
// que ahora devuelven 404 sin período de convivencia.
import { getServicesDjango } from '../services/BusinessUnitServices'
import { useAppSelector } from '../store/hooks'

/**
 * Servicios que el usuario puede elegir.
 *
 * ANTES eran TRES llamadas y una intersección en el cliente: servicios por
 * modalidad de pago ∩ servicios por tipo de entrega − servicios bloqueados para
 * la aplicación. AHORA es UNA sola llamada: el backend combina los filtros y ya
 * devuelve la lista final.
 *
 * BLOQUEO DE SERVICIOS — LEER ANTES DE TOCAR
 * ------------------------------------------
 * El endpoint `locked-by-application` desapareció; el bloqueo se resuelve con los
 * parámetros `tier` (zona) y `store` (tienda).
 *
 * `store` se manda: es la tienda origen del cliente en sesión.
 *
 * `tier` NO se manda, y es una decisión consciente, no un olvido: en este front
 * el tier es un dato POR GUÍA (`GetTierRequestDto`, derivado de origen→destino),
 * no del usuario ni de la sesión. Este hook corre a nivel de pantalla, antes de
 * que exista un destino, así que no hay tier que mandar. Inventar uno filtraría
 * de más o de menos.
 *
 * Consecuencia: los servicios bloqueados POR ZONA no se filtran acá. El backend
 * sigue siendo la barrera autoritativa y rechaza igual un servicio bloqueado al
 * crear la guía, así que esto no abre un agujero de seguridad — pero sí puede
 * dejar seleccionable algo que después se rechace. Ver TODOS.md.
 */
export function useAllowedServices(paymentMode?: PaymentModeId) {
  const user = useAppSelector((s) => s.user)
  const effectiveMode = paymentMode ?? PaymentModeId.CREDIT
  const store = user?.client?.BuCodeSource
  const accountCode = user?.client?.accountCode

  const [services, setServices] = useState<ServiceDTO[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      // La lista de servicios es la barrera de UX del cliente: un fallo
      // transitorio NO debe dejar la pantalla colgada ni "abrir" la lista en
      // silencio. Se reintenta una vez; si aún falla, se avisa por consola y se
      // sigue con lista vacía —el backend es la barrera autoritativa—.
      let res = await getServicesDjango({
        paymentMode: effectiveMode,
        deliveryType: DeliveryEnum.OFFICE,
        accountCode: accountCode || undefined,
        store: store || undefined,
      })
      if (res.didError) {
        res = await getServicesDjango({
          paymentMode: effectiveMode,
          deliveryType: DeliveryEnum.OFFICE,
          accountCode: accountCode || undefined,
          store: store || undefined,
        })
      }
      if (cancelled) return

      if (!res.didError && Array.isArray(res.model)) {
        setServices(res.model)
      } else {
        console.warn(
          '[useAllowedServices] no se pudieron cargar los servicios permitidos; ' +
            'el filtrado queda delegado al backend.'
        )
      }
      setLoaded(true)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [effectiveMode, accountCode, store])

  /**
   * DERIVADO, no estado. Cuando esto era un `useState` alimentado por un efecto,
   * quedaba una ventana de UN render donde `loaded` ya era true pero la lista
   * seguía vacía: cualquier consumidor que confiara en `loaded` para decidir
   * "este usuario no tiene servicios" leía la lista intermedia y decidía mal
   * (bloqueo falso en carga masiva). Con `useMemo`, `loaded === true` implica
   * lista final en el MISMO render.
   */
  const allowedServices = useMemo(
    () => (loaded ? services : []),
    [loaded, services]
  )

  return { allowedServices, loaded }
}
